/**
 * VisibilitySection - Shared visibility dropdown with approval info
 * Used by both FAQ and Document dialogs
 */

import React from 'react';
import { FormControl, InputLabel, Select, MenuItem, Box, Typography, Alert } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface VisibilitySectionProps {
  /** Current visibility value */
  visibility: string;
  /** Callback when visibility changes */
  onChange: (visibility: string) => void;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Error state */
  error?: boolean;
}

export const VisibilitySection: React.FC<VisibilitySectionProps> = ({
  visibility,
  onChange,
  disabled = false,
  error = false
}) => {
  const { t } = useTranslation();

  const handleSelectChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    onChange(event.target.value as string);
  };

  return (
    <Box>
      {/* Approval Info Alert */}
      {(visibility === 'public' || visibility === 'organization') && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {visibility === 'public' 
            ? t('faq.visibility.public_desc', 'Für alle sichtbar (erfordert Superadmin-Genehmigung)')
            : t('faq.visibility.organization_desc', 'Sichtbar für alle Organisationsmitglieder (erfordert Genehmigung)')
          }
        </Alert>
      )}

      <FormControl fullWidth margin="normal">
        <InputLabel>{t('documents.visibility.label', 'Sichtbarkeit')}</InputLabel>
        <Select
          value={visibility}
          onChange={handleSelectChange as any}
          disabled={disabled}
          error={error}
          label={t('documents.visibility.label', 'Sichtbarkeit')}
        >
          <MenuItem value="private">
            <Box>
              <Typography>{t('documents.visibility.private')}</Typography>
              <Typography variant="caption" color="text.secondary">
                {t('faq.visibility.private_desc', 'Nur für Sie sichtbar')}
              </Typography>
            </Box>
          </MenuItem>
          <MenuItem value="organization">
            <Box>
              <Typography>{t('documents.visibility.organization')}</Typography>
              <Typography variant="caption" color="text.secondary">
                {t('faq.visibility.organization_desc', 'Sichtbar für alle Organisationsmitglieder (erfordert Genehmigung)')}
              </Typography>
            </Box>
          </MenuItem>
          <MenuItem value="public">
            <Box>
              <Typography>{t('documents.visibility.public')}</Typography>
              <Typography variant="caption" color="text.secondary">
                {t('faq.visibility.public_desc', 'Für alle sichtbar (erfordert Superadmin-Genehmigung)')}
              </Typography>
            </Box>
          </MenuItem>
        </Select>
      </FormControl>
    </Box>
  );
};

export default VisibilitySection;